// MultiPlayerBingoPage.tsx

import React, { FC, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Tab,
  Tabs,
  ListGroup,
  Stack,
} from "react-bootstrap";
import {
  People,
  Gear,
  PlusCircle,
  Trash,
  Dice5,
  CheckCircleFill,
  ArrowClockwise,
  Printer,
} from "react-bootstrap-icons";

import {
  allContentSets,
  awsWebServicesContent,
  BingoContentSet,
} from "./BingoGameContent_v01a";

type BingoMode = "numbers" | "custom";

interface GameConfig {
  mode: BingoMode;
  gridSize: number; // 3–5
  maxNumber: number; // used in numbers mode
  freeCenter: boolean;
}

interface BingoItem {
  id: string;
  label: string;
}

interface BingoCell {
  itemId: string;
  label: string;
}

interface Player {
  id: string;
  name: string;
  card: BingoCell[];
}

const FREE_SPACE_ID = "__FREE__";
const MANUAL_CONTENT_ID = "manual";

const BingoGamePrint_2025_12_11_chat_v01: FC = () => {
  const [config, setConfig] = useState<GameConfig>({
    mode: "numbers",
    gridSize: 5,
    maxNumber: 75,
    freeCenter: true,
  });

  // default manual text = AWS services list
  const [customItemsText, setCustomItemsText] = useState<string>(
    awsWebServicesContent.items.join(", ")
  );

  const [selectedContentId, setSelectedContentId] = useState<string>(
    awsWebServicesContent.id
  );

  const [players, setPlayers] = useState<Player[]>([]);
  const [newPlayerName, setNewPlayerName] = useState<string>("");
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);

  const [calledItemIds, setCalledItemIds] = useState<string[]>([]);
  const [lastDrawnItem, setLastDrawnItem] = useState<BingoItem | null>(null);

  // Build base items based on mode + selected content
  const baseItems: BingoItem[] = useMemo(() => {
    if (config.mode === "numbers") {
      const max = Math.max(1, config.maxNumber);
      return Array.from({ length: max }, (_, i) => ({
        id: `n-${i + 1}`,
        label: String(i + 1),
      }));
    }

    // CUSTOM MODE: use external content sets or manual text
    let labels: string[] = [];

    if (selectedContentId === MANUAL_CONTENT_ID) {
      labels = parseCustomText(customItemsText);
    } else {
      const preset: BingoContentSet | undefined = allContentSets.find(
        (s) => s.id === selectedContentId
      );
      labels = preset ? preset.items : [];
    }

    const unique = Array.from(new Set(labels));
    return unique.map((label, idx) => ({
      id: `c-${idx}`,
      label,
    }));
  }, [config.mode, config.maxNumber, customItemsText, selectedContentId]);

  const calledSet = useMemo(
    () => new Set<string>(calledItemIds),
    [calledItemIds]
  );

  const remainingItems = useMemo(() => {
    const called = new Set(calledItemIds);
    return baseItems.filter((item) => !called.has(item.id));
  }, [baseItems, calledItemIds]);

  const resetGameState = () => {
    setPlayers([]);
    setCalledItemIds([]);
    setLastDrawnItem(null);
    setActivePlayerId(null);
  };

  const handleApplyConfig = () => {
    resetGameState();
  };

  const handleAddPlayer = () => {
    const name = newPlayerName.trim();
    if (!name) return;
    if (baseItems.length === 0) return;

    const card = createBingoCard(baseItems, config.gridSize, config.freeCenter);

    const newPlayer: Player = {
      id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      card,
    };

    setPlayers((prev) => [...prev, newPlayer]);
    setNewPlayerName("");

    if (!activePlayerId) {
      setActivePlayerId(newPlayer.id);
    }
  };

  const handleRemovePlayer = (playerId: string) => {
    setPlayers((prev) => prev.filter((p) => p.id !== playerId));
    if (activePlayerId === playerId) {
      const remaining = players.filter((p) => p.id !== playerId);
      setActivePlayerId(remaining[0]?.id ?? null);
    }
  };

  const handleDrawNext = () => {
    if (remainingItems.length === 0) return;
    const idx = Math.floor(Math.random() * remainingItems.length);
    const item = remainingItems[idx];

    setCalledItemIds((prev) => [...prev, item.id]);
    setLastDrawnItem(item);
  };

  const handleResetCalls = () => {
    setCalledItemIds([]);
    setLastDrawnItem(null);
  };

  const activePlayer =
    players.find((p) => p.id === activePlayerId) || players[0] || null;

  const playerStatuses = useMemo(
    () =>
      players.map((p) => {
        const result = evaluateCard(p.card, calledSet, config.gridSize);
        return { playerId: p.id, ...result };
      }),
    [players, calledSet, config.gridSize]
  );

  const getPlayerStatus = (playerId: string) =>
    playerStatuses.find((s) => s.playerId === playerId);

  const selectedPreset =
    selectedContentId !== MANUAL_CONTENT_ID
      ? allContentSets.find((s) => s.id === selectedContentId)
      : undefined;

  // ---- PRINT SUPPORT ----

  const getPrintTitle = (): string => {
    if (config.mode === "numbers") {
      return "Number Bingo Card";
    }
    if (config.mode === "custom") {
      if (selectedPreset) {
        return selectedPreset.printTitle || `${selectedPreset.name} Bingo`;
      }
      if (selectedContentId === MANUAL_CONTENT_ID) {
        return "Custom Bingo Card";
      }
    }
    return "Bingo Card";
  };

  const handlePrintActiveCard = () => {
    if (!activePlayer) return;

    const title = getPrintTitle();
    const total = config.gridSize * config.gridSize;

    const cellsHtml = activePlayer.card
      .slice(0, total)
      .map((cell) => {
        const label = escapeHtml(cell.label);
        return `
          <div class="cell">
            <span>${label}</span>
          </div>
        `;
      })
      .join("");

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(title)}</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" />
          <style>
            @page {
              size: Letter;
              margin: 0.5in;
            }
            body {
              margin: 0;
              font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            }
            .print-container {
              width: 100%;
              height: 100%;
              box-sizing: border-box;
              padding: 0.25in;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
            }
            h1 {
              text-align: center;
              margin-bottom: 0.5in;
              font-size: 32px;
              font-weight: 700;
            }
            .grid {
              display: grid;
              grid-template-columns: repeat(${config.gridSize}, 1fr);
              gap: 8px;
              width: 100%;
              max-width: 7.5in;
            }
            .cell {
              border: 2px solid #000;
              min-height: 1.2in;
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: center;
              padding: 4px;
              box-sizing: border-box;
              font-size: 16px;
              font-weight: 600;
              word-wrap: break-word;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <h1>${escapeHtml(title)}</h1>
            <div class="grid">
              ${cellsHtml}
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  // ---- RENDER ----

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <h2 className="d-flex align-items-center gap-2">
            <Dice5 />
            Multiplayer Bingo Arena
          </h2>
          <p className="text-muted mb-0">
            Host a game, add players, customize cards, and draw items in real time.
          </p>
        </Col>
      </Row>

      <Row>
        {/* Left: Configuration */}
        <Col lg={4} className="mb-3">
          <Card>
            <Card.Header className="d-flex align-items-center gap-2">
              <Gear />
              Game Settings
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Mode</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      id="mode-numbers"
                      label="Number Bingo"
                      checked={config.mode === "numbers"}
                      onChange={() =>
                        setConfig((c) => ({ ...c, mode: "numbers" }))
                      }
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="mode-custom"
                      label="Custom / Themed"
                      checked={config.mode === "custom"}
                      onChange={() =>
                        setConfig((c) => ({ ...c, mode: "custom" }))
                      }
                    />
                  </div>
                </Form.Group>

                <Row className="mb-3">
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label>Grid Size</Form.Label>
                      <Form.Select
                        value={config.gridSize}
                        onChange={(e) =>
                          setConfig((c) => ({
                            ...c,
                            gridSize: Number(e.target.value),
                          }))
                        }
                      >
                        <option value={3}>3 × 3</option>
                        <option value={4}>4 × 4</option>
                        <option value={5}>5 × 5</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {config.mode === "numbers" && (
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Max Number</Form.Label>
                        <Form.Control
                          type="number"
                          min={config.gridSize * config.gridSize}
                          value={config.maxNumber}
                          onChange={(e) =>
                            setConfig((c) => ({
                              ...c,
                              maxNumber: Number(e.target.value) || 1,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                {config.mode === "custom" && (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Label>Content Set</Form.Label>
                      <Form.Select
                        value={selectedContentId}
                        onChange={(e) =>
                          setSelectedContentId(e.target.value)
                        }
                      >
                        {allContentSets.map((set) => (
                          <option key={set.id} value={set.id}>
                            {set.name}
                          </option>
                        ))}
                        <option value={MANUAL_CONTENT_ID}>
                          Custom (manual list)
                        </option>
                      </Form.Select>
                      {selectedPreset && (
                        <Form.Text className="d-block text-muted">
                          {selectedPreset.description}
                        </Form.Text>
                      )}
                    </Form.Group>

                    {selectedContentId === MANUAL_CONTENT_ID && (
                      <Form.Group className="mb-3">
                        <Form.Label>Custom Items</Form.Label>
                        <Form.Text className="d-block mb-1">
                          Separate with commas or new lines.
                        </Form.Text>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          value={customItemsText}
                          onChange={(e) => setCustomItemsText(e.target.value)}
                        />
                      </Form.Group>
                    )}

                    {selectedContentId !== MANUAL_CONTENT_ID && selectedPreset && (
                      <Form.Text className="d-block mb-3 text-muted">
                        Using <strong>{selectedPreset.name}</strong> content from{" "}
                        <code>bingoContent.ts</code>.
                      </Form.Text>
                    )}
                  </>
                )}

                {config.gridSize % 2 === 1 && (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="free-center"
                      label="Include FREE space in center"
                      checked={config.freeCenter}
                      onChange={(e) =>
                        setConfig((c) => ({
                          ...c,
                          freeCenter: e.target.checked,
                        }))
                      }
                    />
                  </Form.Group>
                )}

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="small text-muted">
                      Item pool size: <strong>{baseItems.length}</strong>
                    </div>
                    <div className="small text-muted">
                      Called: <strong>{calledItemIds.length}</strong> | Remaining:{" "}
                      <strong>{remainingItems.length}</strong>
                    </div>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleApplyConfig}
                  >
                    Apply &amp; Reset
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Players status summary */}
          <Card className="mt-3">
            <Card.Header className="d-flex align-items-center gap-2">
              <People />
              Player Status
            </Card.Header>
            <Card.Body>
              {players.length === 0 && (
                <div className="text-muted small">
                  No players yet. Add a player on the Players tab.
                </div>
              )}
              <ListGroup variant="flush">
                {players.map((p) => {
                  const status = getPlayerStatus(p.id);
                  return (
                    <ListGroup.Item
                      key={p.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>{p.name}</span>
                      {status && (
                        <Stack direction="horizontal" gap={2}>
                          {status.hasBingo && (
                            <Badge bg="success" pill>
                              <CheckCircleFill className="me-1" />
                              BINGO
                            </Badge>
                          )}
                          <Badge bg="secondary" pill>
                            Lines: {status.completedLines}
                          </Badge>
                        </Stack>
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Tabs - Players / Caller */}
        <Col lg={8}>
          <Tabs defaultActiveKey="players" className="mb-3">
            <Tab eventKey="players" title="Players">
              <Row>
                <Col md={4} className="mb-3">
                  <Card>
                    <Card.Header className="d-flex align-items-center gap-2">
                      <People />
                      Manage Players
                    </Card.Header>
                    <Card.Body>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleAddPlayer();
                        }}
                      >
                        <Form.Group className="mb-2">
                          <Form.Label>New Player Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. Rick"
                            value={newPlayerName}
                            onChange={(e) =>
                              setNewPlayerName(e.target.value)
                            }
                          />
                        </Form.Group>
                        <Button
                          variant="primary"
                          className="w-100 mb-3"
                          onClick={handleAddPlayer}
                          disabled={!newPlayerName.trim() || baseItems.length === 0}
                        >
                          <PlusCircle className="me-1" />
                          Add Player
                        </Button>
                      </Form>

                      <ListGroup style={{ maxHeight: 260, overflowY: "auto" }}>
                        {players.map((p) => {
                          const status = getPlayerStatus(p.id);
                          const isActive = p.id === activePlayerId;
                          return (
                            <ListGroup.Item
                              key={p.id}
                              action
                              active={isActive}
                              onClick={() => setActivePlayerId(p.id)}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <div>{p.name}</div>
                                {status && (
                                  <div className="small">
                                    {status.hasBingo ? (
                                      <span className="text-success">
                                        <CheckCircleFill className="me-1" />
                                        Bingo! ({status.completedLines} lines)
                                      </span>
                                    ) : (
                                      <span className="text-muted">
                                        Lines: {status.completedLines}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Button
                                variant={isActive ? "light" : "outline-danger"}
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePlayer(p.id);
                                }}
                              >
                                <Trash />
                              </Button>
                            </ListGroup.Item>
                          );
                        })}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={8}>
                  <Card className="h-100">
                    <Card.Header>
                      {activePlayer ? (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            {activePlayer.name}&rsquo;s Card{" "}
                            <Badge bg="secondary" className="ms-1">
                              {config.gridSize} × {config.gridSize}
                            </Badge>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {getPlayerStatus(activePlayer.id)?.hasBingo && (
                              <Badge bg="success">
                                <CheckCircleFill className="me-1" />
                                BINGO!
                              </Badge>
                            )}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={handlePrintActiveCard}
                            >
                              <Printer className="me-1" />
                              Print Card
                            </Button>
                          </div>
                        </div>
                      ) : (
                        "No active player"
                      )}
                    </Card.Header>
                    <Card.Body className="d-flex flex-column justify-content-center">
                      {activePlayer ? (
                        <BingoCardGrid
                          card={activePlayer.card}
                          gridSize={config.gridSize}
                          calledSet={calledSet}
                        />
                      ) : (
                        <div className="text-center text-muted">
                          Add a player to generate a Bingo card.
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="caller" title="Caller">
              <Row>
                <Col md={6} className="mb-3">
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <span>Caller Controls</span>
                      <Badge bg="secondary">
                        Remaining: {remainingItems.length}
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3 text-center">
                        <div className="text-muted small mb-1">
                          Last drawn item
                        </div>
                        <h1>
                          {lastDrawnItem ? lastDrawnItem.label : "--"}
                        </h1>
                      </div>
                      <Stack direction="horizontal" gap={2} className="mb-3">
                        <Button
                          variant="primary"
                          className="flex-grow-1"
                          onClick={handleDrawNext}
                          disabled={remainingItems.length === 0}
                        >
                          <Dice5 className="me-1" />
                          Draw Next
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={handleResetCalls}
                        >
                          <ArrowClockwise className="me-1" />
                          Reset Calls
                        </Button>
                      </Stack>
                      {remainingItems.length === 0 && (
                        <div className="text-center text-muted small">
                          All items have been called. Reset calls to start over.
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header>Call History</Card.Header>
                    <Card.Body style={{ maxHeight: 340, overflowY: "auto" }}>
                      {calledItemIds.length === 0 ? (
                        <div className="text-muted small">
                          No items have been called yet.
                        </div>
                      ) : (
                        <ListGroup numbered>
                          {calledItemIds
                            .map((id) => baseItems.find((i) => i.id === id))
                            .filter((i): i is BingoItem => Boolean(i))
                            .map((item, index) => (
                              <ListGroup.Item key={item.id}>
                                <span className="fw-bold me-2">
                                  {index + 1}.
                                </span>
                                {item.label}
                              </ListGroup.Item>
                            ))}
                        </ListGroup>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

interface BingoCardGridProps {
  card: BingoCell[];
  gridSize: number;
  calledSet: Set<string>;
}

const BingoCardGrid: FC<BingoCardGridProps> = ({
  card,
  gridSize,
  calledSet,
}) => {
  const total = gridSize * gridSize;

  return (
    <div
      className="d-grid gap-2 mx-auto"
      style={{
        gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
        maxWidth: 420,
      }}
    >
      {card.slice(0, total).map((cell, idx) => {
        const isFree = cell.itemId === FREE_SPACE_ID;
        const isCalled = isFree || calledSet.has(cell.itemId);

        return (
          <Button
            key={idx}
            variant={isCalled ? "success" : "outline-secondary"}
            className="fw-bold text-center"
            disabled
          >
            {cell.label}
          </Button>
        );
      })}
    </div>
  );
};

// Helpers

function createBingoCard(
  items: BingoItem[],
  gridSize: number,
  freeCenter: boolean
): BingoCell[] {
  const shuffled = [...items].sort(() => Math.random() - 0.5);
  const needed = gridSize * gridSize;
  const selected = shuffled.slice(0, needed);

  const card: BingoCell[] = selected.map((item) => ({
    itemId: item.id,
    label: item.label,
  }));

  if (freeCenter && gridSize % 2 === 1) {
    const centerIndex = Math.floor(needed / 2);
    card[centerIndex] = {
      itemId: FREE_SPACE_ID,
      label: "FREE",
    };
  }

  return card;
}

function evaluateCard(
  card: BingoCell[],
  calledSet: Set<string>,
  gridSize: number
): { completedLines: number; hasBingo: boolean } {
  const isMarked = (index: number) => {
    const cell = card[index];
    if (!cell) return false;
    return cell.itemId === FREE_SPACE_ID || calledSet.has(cell.itemId);
  };

  let completedLines = 0;

  // Rows
  for (let r = 0; r < gridSize; r++) {
    let all = true;
    for (let c = 0; c < gridSize; c++) {
      if (!isMarked(r * gridSize + c)) {
        all = false;
        break;
      }
    }
    if (all) completedLines++;
  }

  // Columns
  for (let c = 0; c < gridSize; c++) {
    let all = true;
    for (let r = 0; r < gridSize; r++) {
      if (!isMarked(r * gridSize + c)) {
        all = false;
        break;
      }
    }
    if (all) completedLines++;
  }

  // Diagonal (top-left to bottom-right)
  {
    let all = true;
    for (let i = 0; i < gridSize; i++) {
      if (!isMarked(i * gridSize + i)) {
        all = false;
        break;
      }
    }
    if (all) completedLines++;
  }

  // Diagonal (top-right to bottom-left)
  {
    let all = true;
    for (let i = 0; i < gridSize; i++) {
      const idx = i * gridSize + (gridSize - 1 - i);
      if (!isMarked(idx)) {
        all = false;
        break;
      }
    }
    if (all) completedLines++;
  }

  return { completedLines, hasBingo: completedLines > 0 };
}

function parseCustomText(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default BingoGamePrint_2025_12_11_chat_v01;
